import {Component, OnInit, ViewChild} from '@angular/core';
import {MatMenuTrigger} from '@angular/material/menu';
import {Router} from '@angular/router';

import {
  AttachmentType,
  IntercomEventService,
  IntercomEventsByArea,
  KibanaLog,
  KibanaLogsService,
  PermissionsService,
  Q9HtmlEncoderPipe,
  REF_MODEL_STORE,
  RefModelNode,
  RefModelStoreService,
  Refmodel,
  SnackBarService,
  TakeUntilComponentDestroy,
  User,
  constants
} from '@q9elements/ui-kit/common';

import {find, sortBy} from 'lodash';
import {forkJoin, of} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';

import {environment} from '../../environments/environment';
import {AuthService} from '../core/services/auth.service';
import {OUTCOME} from '../new-context-menu/models/context-menu.enum';
import {AttachmentsService} from '../services/dao/attachments.service';
import {RefModelService} from '../services/ref-model.service';
import {SfApiService} from '../services/sf-api.service';
import {getPluralSingleMap} from '../util/single-plural-type-map.util';
import {ImpactTreeComponent} from './impact-tree/impact-tree.component';
import {RmnD3TreeData} from './models/rmn-d3-tree-data.interface';
import {ImpactAssessmentDaoService} from './services/impact-assessment-dao.service';
import {ImpactAssessmentStore} from './services/impact-assessment-store';
import {ImpactAssessmentService} from './services/impact-assessment.service';

@Component({
  selector: 'app-dependency-tree',
  templateUrl: './impact-assessment.component.html',
  styleUrls: ['./impact-assessment.component.scss']
})
export class ImpactAssessmentComponent extends TakeUntilComponentDestroy implements OnInit {
  @ViewChild('contextMenuTrigger', {read: MatMenuTrigger}) contextMenuTrigger: MatMenuTrigger;
  @ViewChild(ImpactTreeComponent) treeComp: ImpactTreeComponent;

  readonly updateOutcome = OUTCOME.UPDATE;
  readonly deleteOutcome = OUTCOME.DELETE;

  get isAnyMenuOptionsAvailable(): boolean {
    return this.canAddStory || this.canOpenDependencyTree || this.canOpenGridView;
  }

  public user: User;
  public treeData: RmnD3TreeData;
  public node: RefModelNode;
  public relationDescription: string;
  public contextMenuPosition = {x: '0px', y: '0px'};
  public descriptionPopupPosition = {x: '0px', y: '0px'};
  public hideRelationDescription = true;
  public hasAccessToRefModel = true;
  public canAddStory: boolean;
  public contextLoading: boolean;
  public canOpenDependencyTree: boolean;
  public canOpenGridView: boolean;
  public hasChangeTicketsLicense = this.permissionsService.hasChangeTicketsLicense;
  public isFree = this.permissionsService.isFree;

  private refModel: Refmodel;
  private userRefModelIds: string[];
  private selectedNodeStories: [];
  private copiedStory: any;
  private menuForGroupingNode: boolean;
  private isProPlus: boolean;

  constructor(
    public authService: AuthService,
    private rmeStore: RefModelStoreService,
    private impactAssessmentStore: ImpactAssessmentStore,
    private impactAssessmentDaoService: ImpactAssessmentDaoService,
    private impactAssessmentService: ImpactAssessmentService,
    private sfService: SfApiService,
    private kibanaLog: KibanaLogsService,
    private refModelService: RefModelService,
    private snackBarService: SnackBarService,
    private attachmentsService: AttachmentsService,
    private router: Router,
    private permissionsService: PermissionsService,
    private encodeHtmlPipe: Q9HtmlEncoderPipe,
    private intercomEventService: IntercomEventService
  ) {
    super();
  }

  ngOnInit(): void {
    this.user = this.rmeStore.user;
    this.subscribeToNodeData();
    this.isProPlus = this.permissionsService.isProPlus() && !this.permissionsService.isPlayground();

    this.getSelectedNodeStories();
    this.getRefModelData();
    this.initRefModelList();
    this.setTreeData();
    this.sendKibanaLog();
    this.getUserRefModels();

    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, this.node);
  }

  private initRefModelList(): void {
    this.refModelService
      .getRefModelsList(true)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(data => {
        const userRefModelIds = [...(data.list || [])]
          .filter(refModel => refModel.access || refModel.viewAll)
          .map(refModelsWithAccess => refModelsWithAccess.id);

        this.rmeStore.set(REF_MODEL_STORE.USER_REF_MODELS_LIST, userRefModelIds);
      });
  }

  sendKibanaLog(): void {
    const logData: KibanaLog = {
      entityId: this.node._id,
      entityType: constants.kibanaLogEntityType.DEPENDENCY_TREE,
      eventType: 'Dependency tree opened'
    };

    this.kibanaLog.sendKibanaLog(logData).subscribe();
  }

  getRefModelData() {
    this.rmeStore
      .select(REF_MODEL_STORE.REF_MODEL)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((refModel: Refmodel) => {
        this.refModel = refModel;
        this.intercomEventService.sendIntercomEvent(
          refModel,
          IntercomEventsByArea.SALESFORCE_METADATA.DEPENDENCY_TREE_OPENED
        );

        if (refModel.external) {
          this.sfService.getSFOrgInfoDataByRefmodel(refModel._id).subscribe();
        }
      });
  }

  getUserRefModels() {
    this.rmeStore
      .select(REF_MODEL_STORE.USER_REF_MODELS_LIST)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((refModelsList: string[]) => (this.userRefModelIds = refModelsList));
  }

  async getTreeNodesDetails(refModelId = this.refModel._id, nodeId: string) {
    return await this.impactAssessmentDaoService
      .fetchDependenciesDetails(refModelId, nodeId)
      .toPromise();
  }

  async setTreeData(): Promise<void> {
    const treeNodeDetails = await this.getTreeNodesDetails(this.refModel._id, this.node._id);

    this.treeData = {
      _id: this.node._id,
      name: this.node.name || this.node.customId,
      children: this.processTreeDetailsData(treeNodeDetails),
      type: this.node.sf?.type || this.refModel.name,
      team: this.node.team,
      refModelName: this.refModel.name,
      refModelId: this.node.refModel
    };
  }

  async loadNodeChildren({node, skip}) {
    if (node.data.hasDependents) {
      return await this.setNodeDetails(node);
    }

    const nodeId = node.parent.data._id ? node.parent.data._id : node.parent.data.nodeId;
    const refModelId = node.parent.data.refModelId;
    const {type: nodeType, internalRefModel} = node.data;
    const pluralSingleMap = getPluralSingleMap();

    const requestData: any = {
      refModelId,
      nodeId,
      internalRefModel,
      skip
    };

    if (internalRefModel) {
      requestData.depRefModelId = node.data.refModelId;
    } else {
      requestData.nodeType = pluralSingleMap.get(nodeType);
      requestData.depRefModelId = this.refModel._id;
    }

    this.impactAssessmentDaoService
      .fetchDependenciesChildren(requestData)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(async dependencyChildren => {
        return this.impactAssessmentStore.set('treeData', dependencyChildren);
      });
  }

  async setNodeDetails(node) {
    const {refModelId, nodeId} = node.data;
    const details = await this.getTreeNodesDetails(refModelId, nodeId);

    const processedDetails = this.processTreeDetailsData(details);
    return this.impactAssessmentStore.set('treeData', {children: processedDetails});
  }

  async labelClicked({nodeId, refModelId}) {
    if (refModelId !== this.refModel._id) {
      await this.authService.fetchUser(refModelId).toPromise();
      await this.refModelService.fetchRefModelMeta(refModelId).toPromise();
    }

    this.impactAssessmentService
      .fetchNodeData(refModelId, nodeId)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((node: RefModelNode) => {
        this.node = node;
        this.setSelectedNode(node);
      });
  }

  openContextMenu({
    node,
    isGroupingNode,
    isRootNode,
    event
  }: {
    node;
    isGroupingNode;
    isRootNode;
    event: MouseEvent;
  }) {
    this.copiedStory = this.impactAssessmentService.fetchCopiedStory();
    this.setMenuConfig(event, node);
    this.canOpenGridView = this.rmeStore.isAuthor && this.isProPlus;
    this.menuForGroupingNode = isGroupingNode;
    this.canOpenDependencyTree = !isRootNode;

    const {data: nodeData} = node;

    if (isGroupingNode) {
      return this.openContextMenuForGroupingNode(nodeData);
    }

    if (!nodeData.nodeId || nodeData.nodeId === this.node._id) {
      return this.openContextForRootOrSelected(nodeData, isRootNode);
    }

    this.canOpenDependencyTree = !isRootNode;

    this.checkNodeData({...nodeData, isRootNode})
      .pipe(this.takeUntilComponentDestroy())
      .subscribe();
    this.contextMenuTrigger.openMenu();
    this.closeContextMenuOnRightClick();
  }

  private openContextMenuForGroupingNode(nodeData) {
    const {refModelId} = nodeData;
    const request = this.userRefModelIds.includes(refModelId)
      ? this.impactAssessmentService.fetchRefModelUsers(refModelId)
      : of(null);

    return request.pipe(this.takeUntilComponentDestroy()).subscribe((refModelUsers: any) => {
      this.canOpenGridView = false;

      if (refModelUsers) {
        this.canOpenGridView = this.checkUserPermission(refModelUsers) && this.isProPlus;
      }

      this.canOpenDependencyTree = false;
      this.canAddStory = false;

      if (this.canOpenGridView) {
        return this.contextMenuTrigger.openMenu();
      }
    });
  }

  private subscribeToNodeData() {
    this.impactAssessmentStore
      .select('nodeData')
      .pipe(take(1))
      .subscribe((node: RefModelNode) => (this.node = node));
  }

  private openContextForRootOrSelected(nodeData, isRootNode) {
    this.canOpenDependencyTree = nodeData.nodeId && !isRootNode;

    if (!nodeData.nodeId && this.node._id !== nodeData._id) {
      const {refModelId, _id: nodeId, team} = nodeData;

      return this.checkNodeData({refModelId, nodeId, team, isRootNode})
        .pipe(
          this.takeUntilComponentDestroy(),
          filter(() => this.isAnyMenuOptionsAvailable)
        )
        .subscribe(() => {
          this.contextMenuTrigger.openMenu();
          this.closeContextMenuOnRightClick();
        });
    }

    const storyNotAttached = this.checkIfStoryNotAttached(this.selectedNodeStories);
    this.canAddStory =
      storyNotAttached &&
      this.rmeStore.isAuthor &&
      this.checkIfNodeAndStoryFromSameTeam(nodeData.team);

    if (this.canAddStory || this.canOpenDependencyTree || this.canOpenGridView) {
      this.contextMenuTrigger.openMenu();
      this.closeContextMenuOnRightClick();
    }
  }

  private checkIfNodeAndStoryFromSameTeam(teamId) {
    return this.copiedStory?.team === teamId;
  }

  private checkNodeData({refModelId, nodeId, team, isRootNode}) {
    this.contextLoading = true;

    let fetchStories = of(null);
    let fetchRefModelData: any = of(null);
    const userIsInRefModel = this.userRefModelIds.includes(refModelId);

    if (refModelId && nodeId) {
      fetchStories = this.impactAssessmentService.fetchStoryAttachments(refModelId, nodeId);
    }

    if (refModelId && userIsInRefModel) {
      fetchRefModelData = this.impactAssessmentService.fetchRefModelUsers(refModelId);
    }

    return forkJoin([fetchStories, fetchRefModelData]).pipe(
      this.takeUntilComponentDestroy<[any] | [[], any]>(),
      tap(([stories, refModelUsers]: [[], any]) => {
        let hasPermission;
        let hasAccess;
        const storyNotAttached = this.checkIfStoryNotAttached(stories);

        if (refModelUsers) {
          hasPermission = this.checkUserPermission(refModelUsers);
          hasAccess = this.checkUserHasAccess(refModelUsers, refModelId);
        }

        this.canOpenGridView = hasPermission && this.isProPlus;
        this.canOpenDependencyTree = hasAccess && !isRootNode;
        this.canAddStory =
          storyNotAttached && hasPermission && this.checkIfNodeAndStoryFromSameTeam(team);

        this.contextLoading = false;

        if (!hasAccess && !this.isAnyMenuOptionsAvailable) {
          this.snackBarService.error('POPUPS.NO_ACCESS');
          this.contextMenuTrigger.closeMenu();
        }
      })
    );
  }

  checkIfStoryNotAttached(stories: []) {
    return !find(stories, ['story.id', this.copiedStory?.story]);
  }

  checkUserPermission({users, refModelOwner}) {
    const userIsNotViewer = !!users.find(
      ({id: userId, refModelRole}) =>
        this.user._id === userId && refModelRole !== constants.userRoles.view
    );
    const userIsOwner = refModelOwner.id === this.user._id;

    return userIsNotViewer || userIsOwner;
  }

  checkUserHasAccess({users}, refModelId) {
    const userInRefModel = !!users.find(({id: userId}) => {
      return this.user._id === userId;
    });

    return userInRefModel || this.userRefModelIds.includes(refModelId);
  }

  private setMenuConfig(event, node) {
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.contextMenuTrigger.menuData = {node: node};
  }

  showRelationDescription({node, event}: {node; event: MouseEvent}) {
    const showBelowMouseEvent = event.clientY + 15 + 'px';

    if (!this.contextMenuTrigger.menuOpen) {
      this.descriptionPopupPosition.x = event.clientX + 'px';
      this.descriptionPopupPosition.y = showBelowMouseEvent;
      this.hideRelationDescription = false;
      this.relationDescription = node.data.relationDescription;
    }
  }

  closeContextMenuOnRightClick() {
    document
      .getElementsByClassName('cdk-overlay-backdrop')[0]
      .addEventListener('contextmenu', (offEvent: any) => {
        offEvent.preventDefault();
        this.contextMenuTrigger.closeMenu();
      });
  }

  openDependencyTree(node) {
    window.open(
      `${environment.REF_MODEL_APP}${constants.impactAssessmentLink}${node.refModelId}/${node.nodeId}`,
      '_blank'
    );
  }

  openGridView(node) {
    const {data} = node;
    const {type} = data;
    let nodeId = data.nodeId ? data.nodeId : data._id;
    let refModelId = data.refModelId;
    let dependentId;

    if (!nodeId && this.menuForGroupingNode) {
      const {parent} = node;

      nodeId = parent.data.nodeId ? parent.data.nodeId : parent.data._id;
      dependentId = data.refModelId;
      refModelId = parent.data.refModelId;
    }

    this.treeComp.ngOnDestroy();

    return this.router.navigate(['impact-grid-view', refModelId], {
      queryParams: {
        nodes: [JSON.stringify([nodeId])],
        dependencyTree: true,
        type,
        dependentId,
        grouping: this.menuForGroupingNode
      }
    });
  }

  setSelectedNode(node) {
    this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, node);
  }

  getSelectedNodeStories() {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS)
      .pipe(
        filter(
          (attachment: any) =>
            attachment &&
            (!attachment.loading || (attachment.loading && attachment.generalAttachments))
        ),
        this.takeUntilComponentDestroy()
      )
      .subscribe(({storyAttachments}) => (this.selectedNodeStories = storyAttachments));
  }

  processTreeDetailsData(detailsData) {
    const pluralSingleMap = getPluralSingleMap(true);
    const mappedDetails = detailsData.parentSfTypes.map(typeWithCount => ({
      type: pluralSingleMap.get(typeWithCount.type) || typeWithCount.type,
      refModelId: typeWithCount.refModelId,
      internalRefModel: typeWithCount.internalRefModel,
      count: typeWithCount.count,
      children: typeWithCount.children || []
    }));

    return sortBy([...mappedDetails], 'type');
  }

  addNodeToStory(outcome: OUTCOME.UPDATE | OUTCOME.DELETE, node) {
    this.impactAssessmentService
      .getGroups(this.copiedStory.team)
      .pipe(
        this.takeUntilComponentDestroy(),
        map(groups => [find(groups, ['systemType', 'all'])._id])
      )
      .subscribe((groups: []) => this.createStoryLink(outcome, node, groups));
  }

  private createStoryLink(outcome: OUTCOME.UPDATE | OUTCOME.DELETE, node, groups = []) {
    const {data: nodeData} = node;
    const nodeId = nodeData.nodeId ? nodeData.nodeId : nodeData._id;
    const url = `${environment.API_ENDPOINT}/refmodel/${nodeData.refModelId}/node/${nodeId}`;

    const params = this.buildReqParams(outcome, node, groups);

    this.impactAssessmentService
      .createStoryLink(url, params)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(
        res => {
          this.snackBarService.error('NOTIFICATIONS.NODE_ADDED_TO_STORY');

          if (this.node._id === nodeId) {
            this.attachmentsService.pushAttachmentInStore(AttachmentType.STORY, res);
          }
        },
        reject => this.snackBarService.error(reject.error)
      );
  }

  buildReqParams(outcome: OUTCOME.UPDATE | OUTCOME.DELETE, node, groups) {
    const {storyId, story, name} = this.copiedStory;
    const shortenedTitle = name.substring(80, 0);

    return {
      outcome,
      story,
      storyId,
      groups,
      description: this.getDependencyNodeDescription(node),
      title: this.decode(shortenedTitle)
    };
  }

  getDependencyNodeDescription(node) {
    const newLinePlusEmpty = '\n\n</br></br>';
    const {data: nodeData, parent: nodeParent} = node;
    const parentName = nodeParent?.parent?.data?.name;
    let description = `Node name: ${nodeData.name}`;

    if (nodeData.relationDescription) {
      description = `${description}${newLinePlusEmpty}Relationship description: ${parentName} ${nodeData.relationDescription}</br>`;
    }

    return description;
  }

  private decode(string = '') {
    return this.encodeHtmlPipe.transform(string, true);
  }
}
